/**
 * Catálogo de permissões. Cada rota da API exige uma delas; o painel usa as mesmas
 * chaves para esconder ações. `super_admin` tem todas, sempre; `admin`, `operator` e
 * `viewer` começam com o padrão abaixo e podem ser ajustados em Perfis e permissões
 * (o perfil `admin` só pelo Super Administrador).
 */
export const PERMISSIONS = [
    { key: 'dashboard.read', feature: 'Painel', description: 'Ver o painel de métricas' },
    { key: 'leads.read', feature: 'Leads', description: 'Ver leads e histórico de chamadas' },
    {
        key: 'leads.manage',
        feature: 'Leads',
        description: 'Criar, editar, importar e mudar status de leads',
    },
    { key: 'leads.call', feature: 'Leads', description: 'Ligar agora (telefone ou navegador)' },
    { key: 'leads.delete', feature: 'Leads', description: 'Excluir leads' },
    { key: 'campaigns.read', feature: 'Campanhas', description: 'Ver campanhas e progresso' },
    {
        key: 'campaigns.manage',
        feature: 'Campanhas',
        description: 'Criar, iniciar e pausar campanhas',
    },
    { key: 'campaigns.delete', feature: 'Campanhas', description: 'Excluir campanhas' },
    { key: 'calls.read', feature: 'Chamadas', description: 'Ver chamadas, transcrições e gravações' },
    {
        key: 'calls.manage',
        feature: 'Chamadas',
        description: 'Ressincronizar com o provedor e usar o simulador',
    },
    { key: 'scripts.read', feature: 'Scripts', description: 'Ver scripts e pré-visualizar' },
    { key: 'scripts.manage', feature: 'Scripts', description: 'Criar e editar scripts' },
    { key: 'scripts.delete', feature: 'Scripts', description: 'Excluir scripts' },
    { key: 'products.read', feature: 'Produtos', description: 'Ver produtos' },
    { key: 'products.manage', feature: 'Produtos', description: 'Criar e editar produtos' },
    { key: 'products.delete', feature: 'Produtos', description: 'Excluir produtos' },
    {
        key: 'settings.read',
        feature: 'Configurações',
        description: 'Ver configurações (sem segredos)',
    },
    {
        key: 'settings.manage',
        feature: 'Configurações',
        description: 'Alterar configurações e chaves de API',
    },
    {
        key: 'webhooks.read',
        feature: 'Configurações',
        description: 'Ver eventos de webhook recebidos',
    },
    { key: 'users.read', feature: 'Usuários', description: 'Ver usuários e sessões' },
    {
        key: 'users.manage',
        feature: 'Usuários',
        description: 'Criar, editar, desativar e redefinir senha',
    },
    {
        key: 'roles.read',
        feature: 'Perfis e permissões',
        description: 'Ver permissões de cada perfil',
    },
    {
        key: 'roles.manage',
        feature: 'Perfis e permissões',
        description: 'Alterar permissões dos perfis',
    },
    { key: 'audit.read', feature: 'Auditoria', description: 'Ver a trilha de auditoria' },
];
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
export const ROLE_LABELS = {
    super_admin: 'Super Administrador',
    admin: 'Administrador',
    operator: 'Operador',
    viewer: 'Visualizador',
};
export const ROLE_DESCRIPTIONS = {
    super_admin: 'Acima de tudo: todas as permissões, sempre. Único que gerencia administradores e o perfil deles.',
    admin: 'Gestão completa da operação e dos usuários. O Super Administrador pode restringir.',
    operator: 'Opera o dia a dia: leads, ligações, campanhas, scripts e produtos.',
    viewer: 'Somente leitura.',
};
/** Hierarquia: menor = mais poder. */
export const ROLE_RANK = {
    super_admin: 0,
    admin: 1,
    operator: 2,
    viewer: 3,
};
/**
 * Quem pode criar, editar, redefinir a senha ou excluir usuários de um perfil, e atribuir esse
 * perfil. Super Administrador só é tocado por outro Super Administrador; administradores por
 * administradores ou acima; os demais por quem tiver `users.manage`.
 */
export function canManageRole(actor, target) {
    if (target === 'super_admin')
        return actor === 'super_admin';
    if (target === 'admin')
        return actor === 'super_admin' || actor === 'admin';
    return true;
}
/**
 * Quem pode editar as permissões de um perfil (além de ter `roles.manage`).
 * O Super Administrador é fixo; o perfil Administrador só o Super Administrador altera.
 */
export function canEditRolePermissions(actor, target) {
    if (target === 'super_admin')
        return false;
    if (target === 'admin')
        return actor === 'super_admin';
    return true;
}
export const DEFAULT_ROLE_PERMISSIONS = {
    super_admin: [...PERMISSION_KEYS],
    admin: [...PERMISSION_KEYS],
    operator: [
        'dashboard.read',
        'leads.read',
        'leads.manage',
        'leads.call',
        'campaigns.read',
        'campaigns.manage',
        'calls.read',
        'calls.manage',
        'scripts.read',
        'scripts.manage',
        'products.read',
        'products.manage',
        'settings.read',
        'webhooks.read',
    ],
    viewer: [
        'dashboard.read',
        'leads.read',
        'campaigns.read',
        'calls.read',
        'scripts.read',
        'products.read',
        'settings.read',
    ],
};
/** `super_admin` tem tudo, sempre; os demais (inclusive `admin`) dependem da lista configurada. */
export function hasPermission(user, ...keys) {
    if (!user)
        return false;
    if (user.role === 'super_admin')
        return true;
    return keys.every((k) => user.permissions.includes(k));
}
